<template>
    <div class="ContractExtractionRequirements">
        <div v-if="isLoading" class="ContractExtractionRequirements__body">
            Loading requirements...
        </div>
        <div v-else class="ContractExtractionRequirements__body">
            <RequirementsEditorToolbar
                :show-title="false"
                :available-requirement-options="availableRequirementTypesOptions"
                :has-evaluation-rule="isEvaluationRuleRequirementTypesSelected"
                :can-add-requirement="canManageRequirements"
                :can-edit-evaluation-rule="canManageRequirements"
                @add="addRequirementType"
                @toggle-evaluation-rule-modal="toggleEvaluationRuleModal"
            />
            <RequirementsEditorList
                :criteria-by-type="criteriaByRequirementType"
                :expanded-requirement-types="expandedRequirementTypes"
                :has-deprecated-criteria-by-requirement-type="hasDeprecatedCriteriaByRequirementType"
                :get-requirement-type-label="getRequirementTypeLabel"
                :is-requirement-type-in-evaluation-rule="isRequirementTypeInEvaluationRule"
                :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
                :templates-by-requirement-type="templatesByRequirementType"
                :criteria-templates-by-requirement-type="criteriaTemplatesByRequirementType"
                :custom-properties="customProperties"
                :country-code="countryCode"
                :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
                :show-validation-errors="showValidationErrors"
                :can-delete-requirement-type="canManageRequirements"
                :can-manage-criteria="canManageRequirements"
                :can-open-criterion-settings="canManageRequirements"
                @delete="tryDeleteRequirementType"
                @expand="expandedRequirementTypes = $event"
                @criteria-input="updateCriteria"
                @reference-accessed="onReferenceAccessed"
                @validity-change="onValidityChanged"
            />
        </div>

        <EvaluationRuleModal
            v-if="showEvaluationRuleModal"
            :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
            :get-requirement-type-label="getRequirementTypeLabel"
            :criteria-by-requirement-type="criteriaByRequirementType"
            :is-evaluation-rule-requirement-types-selected="isEvaluationRuleRequirementTypesSelected"
            @save="onSaveEvaluationRule"
            @delete="showEvaluationRuleConfirmDeleteModal"
            @close="toggleEvaluationRuleModal"
        />
        <ConfirmationModal
            v-if="confirmationModalData.text"
            additional-class-name="ContractExtractionRequirements__confirmation-modal"
            title="Are you sure?"
            yes="Delete"
            no="Cancel"
            :destructive="true"
            @input="onConfirmDelete"
        >
            {{ confirmationModalData.text }}
        </ConfirmationModal>
    </div>
</template>

<style lang="scss">
.ContractExtractionRequirements {
    height: 100%;

    &__body {
        height: 100%;

        .RequirementTypeList__header {
            padding: 0 0 14px;
            border-bottom: 0;
        }

        .RequirementTypeList__content {
            padding: 0;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { flatten, omit, startCase } from 'lodash';
    import difference from 'lodash/difference';

    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown';
    import { ContractExtractionCriterion } from '@evidentid/tprm-portal-lib/models/contract-extraction';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { TprmRequirementModel } from '@evidentid/tprm-portal-lib/models/entity-details/TprmRequirementModel.model';
    import { toTprmRequirementType } from '@evidentid/tprm-portal-lib/utils/common/toTprmRequirementType';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import ConfirmationModal from '@/components/confirmation-modal/ConfirmationModal.vue';
    import { useContractExtractionStore } from '@/modules/contract-extraction/store';
    import EvaluationRuleModal from '@/modules/decisioning-criteria/components/EvaluationRuleModal/EvaluationRuleModal.vue';
    import RequirementsEditorList
        from '@/modules/decisioning-criteria/components/RequirementsEditorList/RequirementsEditorList.vue';
    import RequirementsEditorToolbar
        from '@/modules/decisioning-criteria/components/RequirementsEditorToolbar/RequirementsEditorToolbar.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import { requirementTypeFieldReferenceDelimiter } from '@/modules/decisioning-criteria/types';
    import {
        createDefaultEditableCriteriaForRequirementType,
        groupEditableCriteriaByType,
        groupTemplatesByType,
        mapTemplatesByField,
        removeRequirementTypeFromEvaluationRule,
        setEditableCriteriaVerifyState,
        tryAutoAddUmbrellaRequirementType,
    } from '@/modules/decisioning-criteria/utils/requirementsEditorState';
    import {
        syncEditableCriteriaWithTemplates,
    } from '@/modules/decisioning-criteria/utils/syncCriteriaWithTemplates';

    const UMBRELLA_EXCESS_LIABILITY_REQUIREMENT = toTprmRequirementType('UMBRELLA_EXCESS_LIABILITY');

    interface ConfirmationModalData {
        text?: string;
        confirmDeleteFor?: 'coverageType' | 'evaluationRule';
        coverageType?: TprmRequirementType;
        isCoverageTypeInEvaluationRule?: boolean;
    }

    export default defineComponent({
        name: 'ContractExtractionRequirements',
        components: {
            ConfirmationModal,
            EvaluationRuleModal,
            RequirementsEditorList,
            RequirementsEditorToolbar,
        },
        props: {
            rpName: {
                type: String,
                required: true,
            },
            contractExtractionId: {
                type: String,
                required: true,
            },
            criteriaStatus: {
                type: Object as PropType<{
                    status: OperationStatus;
                    originalCriteria: ContractExtractionCriterion[] | null;
                    editableCriteria: EditableRequirementCriterion[] | null;
                    editableEvaluationRuleRequirementTypes: TprmRequirementType[];
                }>,
                required: true,
            },
            requirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            requirementModels: {
                type: Array as PropType<TprmRequirementModel[]>,
                default: (): TprmRequirementModel[] => [],
            },
            criterionTemplates: {
                type: Array as PropType<CriterionTemplate[]>,
                default: (): CriterionTemplate[] => [],
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            countryCode: {
                type: String,
                default: 'US',
            },
            canManageRequirements: {
                type: Boolean,
                default: true,
            },
            showValidationErrors: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
        },
        emits: [ 'has-errors-change' ],
        data() {
            return {
                expandedRequirementTypes: [] as TprmRequirementType[],
                invalidReferences: [] as string[],
                accessedMap: {} as Record<string, boolean>,
                showEvaluationRuleModal: false,
                confirmationModalData: {} as ConfirmationModalData,
                canAutoAddUmbrella: true,
            };
        },
        computed: {
            isLoading(): boolean {
                return this.criteriaStatus.status === OperationStatus.loading
                    || this.criteriaStatus.status === OperationStatus.uninitialized;
            },
            filteredCriterionTemplates(): CriterionTemplate[] {
                return this.criterionTemplates.filter((template) => template.field !== 'coverage.collateral');
            },
            criteriaTemplatesByRequirementType(): Partial<Record<TprmRequirementType, CriterionTemplate[]>> {
                return groupTemplatesByType(this.filteredCriterionTemplates);
            },
            templatesByRequirementType():
                Partial<Record<TprmRequirementType, Record<string, CriterionTemplate>>> {
                return mapTemplatesByField(this.criteriaTemplatesByRequirementType);
            },
            criteriaByRequirementType(): Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>> {
                const synced = syncEditableCriteriaWithTemplates(
                    this.criteriaStatus.editableCriteria || [],
                    this.criterionTemplates,
                );
                return groupEditableCriteriaByType(synced);
            },
            requirementTypesWithTemplates(): TprmRequirementType[] {
                return (
                    Object.keys(this.criteriaTemplatesByRequirementType)
                        .filter((requirementType) =>
                            this.requirementTypes.includes(requirementType as TprmRequirementType),
                        )
                ) as TprmRequirementType[];
            },
            sortedRequirementTypes(): TprmRequirementType[] {
                return [ ...this.requirementTypesWithTemplates ].sort((a, b) =>
                    this.getRequirementTypeLabel(a).localeCompare(this.getRequirementTypeLabel(b)));
            },
            availableRequirementTypesOptions(): DropdownOption<TprmRequirementType>[] {
                return difference(
                    this.sortedRequirementTypes,
                    Object.keys(this.criteriaByRequirementType) as TprmRequirementType[],
                ).map((requirementType) => ({
                    label: this.getRequirementTypeLabel(requirementType),
                    value: requirementType,
                }));
            },
            evaluationRuleRequirementTypes(): TprmRequirementType[] {
                return this.criteriaStatus.editableEvaluationRuleRequirementTypes || [];
            },
            isEvaluationRuleRequirementTypesSelected(): boolean {
                const [ one, two ] = this.evaluationRuleRequirementTypes;
                return Boolean(one) && Boolean(two);
            },
        },
        watch: {
            invalidReferences: {
                immediate: true,
                handler(references: string[]) {
                    this.$emit('has-errors-change', references.length > 0);
                },
            },
        },
        methods: {
            persistCriteria(
                criteriaByRequirementType: Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>>,
            ): void {
                useContractExtractionStore().updateEditableContractExtractionCriteria({
                    rpName: this.rpName,
                    contractExtractionId: this.contractExtractionId,
                    editableCriteria: flatten(Object.values(criteriaByRequirementType)),
                });
            },
            getRequirementTypeLabel(requirementType: TprmRequirementType): string {
                return this.requirementModels.find((model) => model.coverageType === requirementType)?.label
                    || startCase(requirementType.toLowerCase());
            },
            isRequirementTypeInEvaluationRule(requirementType: TprmRequirementType): boolean {
                return this.evaluationRuleRequirementTypes.includes(requirementType);
            },
            hasDeprecatedCriteriaByRequirementType(requirementType: TprmRequirementType): boolean {
                const templates = this.criteriaTemplatesByRequirementType[requirementType] || [];
                const criteria = this.criteriaByRequirementType[requirementType] || [];

                return templates
                    .filter((template) => template.deprecated)
                    .some((template) => criteria.some((criterion) => criterion.field === template.field));
            },
            isReferenceStartsWithIsInvalid(searchString: string): boolean {
                const searchSegments = searchString.split(requirementTypeFieldReferenceDelimiter);

                return this.invalidReferences.some((reference) => {
                    const refSegments = reference.split(requirementTypeFieldReferenceDelimiter);
                    if (searchSegments.length > refSegments.length) {
                        return false;
                    }

                    return searchSegments.every((segment, index) => refSegments[index] === segment);
                });
            },
            addRequirementType(type: TprmRequirementType): void {
                const nextCriteriaByRequirementType: Partial<
                    Record<TprmRequirementType, EditableRequirementCriterion[]>
                > = {
                    ...this.criteriaByRequirementType,
                    [type]: createDefaultEditableCriteriaForRequirementType(
                        type,
                        this.criteriaTemplatesByRequirementType,
                    ),
                };
                const updatedCriteriaByType = tryAutoAddUmbrellaRequirementType({
                    criteriaByRequirementType: nextCriteriaByRequirementType,
                    requirementType: type,
                    currentCriteria: nextCriteriaByRequirementType[type] || [],
                    originalCriteria: [],
                    canAutoAddUmbrella: this.canAutoAddUmbrella,
                    umbrellaRequirementType: UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                    umbrellaCriteria: setEditableCriteriaVerifyState(
                        createDefaultEditableCriteriaForRequirementType(
                            UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                            this.criteriaTemplatesByRequirementType,
                        ),
                        false,
                    ),
                });

                this.persistCriteria(updatedCriteriaByType);

                if (!this.expandedRequirementTypes.includes(type)) {
                    this.expandedRequirementTypes = [ ...this.expandedRequirementTypes, type ];
                }
            },
            updateCriteria(payload: {
                criteria: EditableRequirementCriterion[];
                requirementType: TprmRequirementType;
            }): void {
                const originalCriteria = this.criteriaByRequirementType[payload.requirementType] || [];
                const nextCriteriaByRequirementType = {
                    ...this.criteriaByRequirementType,
                    [payload.requirementType]: payload.criteria,
                };
                const updatedCriteriaByType = tryAutoAddUmbrellaRequirementType({
                    criteriaByRequirementType: nextCriteriaByRequirementType,
                    requirementType: payload.requirementType,
                    currentCriteria: payload.criteria,
                    originalCriteria,
                    canAutoAddUmbrella: this.canAutoAddUmbrella,
                    umbrellaRequirementType: UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                    umbrellaCriteria: setEditableCriteriaVerifyState(
                        createDefaultEditableCriteriaForRequirementType(
                            UMBRELLA_EXCESS_LIABILITY_REQUIREMENT,
                            this.criteriaTemplatesByRequirementType,
                        ),
                        false,
                    ),
                });

                this.persistCriteria(updatedCriteriaByType);
            },
            tryDeleteRequirementType(requirementType: TprmRequirementType): void {
                if (!this.isRequirementTypeInEvaluationRule(requirementType)) {
                    this.deleteRequirementType(requirementType);
                    return;
                }

                this.confirmationModalData = {
                    text: `This requirement is currently assigned to an Evaluation Rule.
                Entities currently assigned to this risk profile may be affected.`,
                    confirmDeleteFor: 'coverageType',
                    coverageType: requirementType,
                    isCoverageTypeInEvaluationRule: true,
                };
            },
            deleteRequirementType(type: TprmRequirementType): void {
                const nextCriteriaByRequirementType = omit(this.criteriaByRequirementType, type);
                this.clearInvalidListPerRequirementType(type);
                this.expandedRequirementTypes = this.expandedRequirementTypes.filter(
                    (expandedType) => expandedType !== type,
                );

                if (type === UMBRELLA_EXCESS_LIABILITY_REQUIREMENT) {
                    this.canAutoAddUmbrella = false;
                }

                if (this.isRequirementTypeInEvaluationRule(type)) {
                    useContractExtractionStore().updateEditableContractExtractionEvaluationRule({
                        rpName: this.rpName,
                        contractExtractionId: this.contractExtractionId,
                        editableEvaluationRuleRequirementTypes: removeRequirementTypeFromEvaluationRule(
                            type,
                            this.evaluationRuleRequirementTypes,
                        ),
                    });
                }

                this.persistCriteria(nextCriteriaByRequirementType);
            },
            toggleEvaluationRuleModal(): void {
                this.showEvaluationRuleModal = !this.showEvaluationRuleModal;
            },
            onSaveEvaluationRule(payload: { requirementTypes: TprmRequirementType[] }): void {
                useContractExtractionStore().updateEditableContractExtractionEvaluationRule({
                    rpName: this.rpName,
                    contractExtractionId: this.contractExtractionId,
                    editableEvaluationRuleRequirementTypes: payload.requirementTypes,
                });
            },
            showEvaluationRuleConfirmDeleteModal(): void {
                this.confirmationModalData = {
                    text: 'Entities currently assigned to this risk profile may be affected.',
                    confirmDeleteFor: 'evaluationRule',
                };
            },
            onConfirmDelete(confirmed: boolean): void {
                const {
                    confirmDeleteFor,
                    coverageType,
                    isCoverageTypeInEvaluationRule,
                } = this.confirmationModalData;
                this.confirmationModalData = {};

                if (!confirmed) {
                    return;
                }

                if (confirmDeleteFor === 'evaluationRule') {
                    this.deleteEvaluationRule();
                    return;
                }

                if (coverageType) {
                    this.deleteRequirementType(coverageType);
                }

                if (isCoverageTypeInEvaluationRule) {
                    this.deleteEvaluationRule();
                }
            },
            deleteEvaluationRule(): void {
                useContractExtractionStore().updateEditableContractExtractionEvaluationRule({
                    rpName: this.rpName,
                    contractExtractionId: this.contractExtractionId,
                    editableEvaluationRuleRequirementTypes: [],
                });
            },
            onReferenceAccessed(data: { reference: string, isValid: boolean }): void {
                this.accessedMap[data.reference] = true;
                this.onValidityChanged(data);
            },
            onValidityChanged(data: { reference: string, isValid: boolean }): void {
                const index = this.invalidReferences.indexOf(data.reference);
                const refStartsWithRegex = new RegExp(`^${data.reference}`);
                if (data.isValid) {
                    this.invalidReferences = this.invalidReferences.filter((ref) => !refStartsWithRegex.test(ref));
                } else if (index < 0) {
                    this.invalidReferences = [ ...this.invalidReferences, data.reference ];
                }
            },
            clearInvalidListPerRequirementType(requirementType: TprmRequirementType): void {
                this.invalidReferences = this.invalidReferences.filter((combinedReference) =>
                    combinedReference.split(requirementTypeFieldReferenceDelimiter)[0] !== requirementType,
                );
            },
        },
    });
</script>
